import { DeviceType, ESdChangeReason, IDisplayItem, IScreenDesignDictionary } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { getScreenDictionary, getScreenDesign, setScreenDesign } from "./designer-utils";
import { type IScreenScopedInput } from "./detached-screen-session";

function normalizeBreakpointArray(arr: number[], targetLength: number): number[] {
  if (arr.length === targetLength) return arr;
  if (arr.length > targetLength) return arr.slice(0, targetLength);

  const result = [...arr];
  const lastValue = arr[arr.length - 1];
  while (result.length < targetLength) {
    result.push(lastValue);
  }
  return result;
}

function mapDeviceType(device: string | undefined): DeviceType {
  switch (device) {
    case "tablet":
      return DeviceType.Tablet;
    case "mobile":
      return DeviceType.Phone;
    default:
      return DeviceType.Desktop;
  }
}

/** Runtime-only interface for dragDrop methods used in responsive actions. Not part of IScreenDesignContextState. */
interface DragDropLike {
  setColumnsNumber: (
    fractions: number[],
    kuikaId: string,
    deviceType: DeviceType,
    cascadeTablet: boolean,
    cascadePhone: boolean
  ) => void;
  resizeColumnValuesByIndex: (
    di: IDisplayItem,
    deviceType: DeviceType,
    columnIndex: number,
    span: number,
    cascadeTablet: boolean,
    cascadePhone: boolean
  ) => void;
}

/** Access the dragDrop instance that exists at runtime on the provider but is not on IScreenDesignContextState. */
function getDragDrop(sdInstance: ScreenDesignInstance): DragDropLike {
  return (sdInstance as unknown as { dragDrop: DragDropLike }).dragDrop;
}

/** Access gridSystemCount from the provider's state or project. */
function getGridSystemCount(sd: IScreenDesignContextState): number {
  const fromState = (sd as unknown as { state?: { project?: { componentSetObj?: { gridSystemCount?: number } } } })
    .state;
  return fromState?.project?.componentSetObj?.gridSystemCount || sd.project?.componentSetObj?.gridSystemCount || 24;
}

/**
 * WHY THESE TWO KEEP `screenId` BUT REFUSE IT.
 *
 * Column layout is not written through the screen triple — it goes through the drag-drop engine
 * (`DragDrop.setColumnsNumber` / `resizeColumnValuesByIndex`), which is constructed with the
 * PROVIDER and reaches the tree as `screenContext.screenProvider.getScreenDesign()`. Routing the
 * reads here to a detached screen while that engine still wrote the open one would validate one
 * screen and rewrite another — the wrong-canvas failure in its purest form.
 *
 * So the member is declared and REFUSED: a caller that means another screen gets a clear error
 * instead of silent damage. Making the drag-drop engine screen-agnostic is the prerequisite for
 * lifting this, and it is not a one-line change (~200 reads of `this.screenContext`).
 */
export interface IResponsiveColumnsInput extends IScreenScopedInput {
  kuikaId: string;
  desktop: number[];
  tablet?: number[];
  mobile?: number[];
}

export interface IResizeResponsiveColumnInput extends IScreenScopedInput {
  rowKuikaId: string;
  columnIndex: number;
  span: number;
  device?: string;
}

/** The refusal shared by both column writers. Null when the input means the open screen. */
function refuseForeignScreen(sdInstance: ScreenDesignInstance, screenId?: string | null): { error: string } | null {
  if (!screenId) return null;
  const live: IScreenDesignContextState = getValues(sdInstance);
  if (screenId === live.sdId) return null;
  return {
    error:
      `Responsive column layout can only be changed on the screen that is OPEN in the designer ` +
      `(asked for "${screenId}"). The drag-drop engine that lays out columns is bound to the open ` +
      `screen, so this call would have measured one screen and rewritten another.`
  };
}

export function setDesignerResponsiveColumns(
  sdInstance: ScreenDesignInstance,
  input: IResponsiveColumnsInput
):
  | { error: string }
  | {
      success: true;
      message: string;
      kuikaId: string;
      columnCount: number;
      desktop: number[];
      tablet: number[] | string;
      mobile: number[] | string;
    } {
  const { kuikaId, desktop, tablet, mobile } = input;
  const foreign: { error: string } | null = refuseForeignScreen(sdInstance, input.screenId);
  if (foreign) return foreign;
  if (!kuikaId) return { error: "kuikaId is required" };
  if (!desktop || !Array.isArray(desktop) || desktop.length === 0) {
    return { error: "desktop must be a non-empty array of column spans (e.g. [12, 12])" };
  }

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const dict: IScreenDesignDictionary = getScreenDictionary(sd);
  const di: IDisplayItem | undefined = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };

  if (di.item?.name !== "Row") {
    return {
      error: `Component "${di.item?.name}" (${kuikaId}) is not a Row. ui_structure_set_responsive_columns only works on Row components.`
    };
  }

  const gridSystemCount = getGridSystemCount(sd);
  const hasTablet = tablet && Array.isArray(tablet) && tablet.length > 0;
  const hasMobile = mobile && Array.isArray(mobile) && mobile.length > 0;

  const dragDrop = getDragDrop(sdInstance);
  const desktopFractions = desktop.map((column: number) => column / gridSystemCount);
  dragDrop.setColumnsNumber(desktopFractions, kuikaId, DeviceType.Desktop, !hasTablet, !hasMobile);

  if (hasTablet) {
    const normalizedTablet = normalizeBreakpointArray(tablet, desktop.length);
    const tabletFractions = normalizedTablet.map((column: number) => column / gridSystemCount);
    dragDrop.setColumnsNumber(tabletFractions, kuikaId, DeviceType.Tablet, false, !hasMobile);
  }

  if (hasMobile) {
    const normalizedMobile = normalizeBreakpointArray(mobile, desktop.length);
    const mobileFractions = normalizedMobile.map((column: number) => column / gridSystemCount);
    dragDrop.setColumnsNumber(mobileFractions, kuikaId, DeviceType.Phone, false, false);
  }

  return {
    success: true,
    message: `Set ${desktop.length} columns on Row (${kuikaId}): desktop=[${desktop.join(",")}]${hasTablet ? `, tablet=[${tablet.join(",")}]` : ""}${hasMobile ? `, mobile=[${mobile.join(",")}]` : ""}`,
    kuikaId,
    columnCount: desktop.length,
    desktop,
    tablet: hasTablet ? tablet : "cascaded from desktop",
    mobile: hasMobile ? mobile : "cascaded"
  };
}

export function resizeDesignerResponsiveColumn(
  sdInstance: ScreenDesignInstance,
  input: IResizeResponsiveColumnInput
):
  | { error: string }
  | {
      success: true;
      message: string;
      rowKuikaId: string;
      columnIndex: number;
      columnKuikaId: string | undefined;
      span: number;
      device: string;
    } {
  const { rowKuikaId, columnIndex, span } = input;
  const device = input.device || "desktop";

  const foreign: { error: string } | null = refuseForeignScreen(sdInstance, input.screenId);
  if (foreign) return foreign;
  if (!rowKuikaId) return { error: "rowKuikaId is required" };
  if (columnIndex === undefined || columnIndex === null) return { error: "columnIndex is required" };
  if (!span && span !== 0) return { error: "span is required" };

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const dict: IScreenDesignDictionary = getScreenDictionary(sd);
  const rowDI: IDisplayItem | undefined = dict[parseInt(rowKuikaId, 10)];
  if (!rowDI) return { error: `Row with kuikaId "${rowKuikaId}" not found` };

  if (rowDI.item?.name !== "Row") {
    return { error: `Component "${rowDI.item?.name}" (${rowKuikaId}) is not a Row.` };
  }

  if (!rowDI.children || columnIndex >= rowDI.children.length) {
    return {
      error: `Column index ${columnIndex} is out of range. Row has ${rowDI.children?.length || 0} columns (0-based).`
    };
  }

  if (span < 1 || span > 24) {
    return { error: `span must be between 1 and 24, got ${span}` };
  }

  const deviceType = mapDeviceType(device);
  const cascadeTablet = device === "desktop";
  const cascadePhone = device === "desktop" || device === "tablet";

  const dragDrop = getDragDrop(sdInstance);
  dragDrop.resizeColumnValuesByIndex(rowDI, deviceType, columnIndex, span, cascadeTablet, cascadePhone);

  const screenDesign = getScreenDesign(sd);
  setScreenDesign(sd, screenDesign, ESdChangeReason.ChangeResponsiveRowColumnSettings, rowKuikaId);

  const colDi: IDisplayItem | undefined = rowDI.children[columnIndex];

  return {
    success: true,
    message: `Resized column ${columnIndex} of Row (${rowKuikaId}) to span ${span} on ${device}`,
    rowKuikaId,
    columnIndex,
    columnKuikaId: colDi?.id?.toString(),
    span,
    device
  };
}
